import { BaseService } from "./baseService";
import { Meeting, MeetingListResponse } from "../types";

class MeetingService extends BaseService<Meeting> {
  constructor() {
    super("Meeting");
    this.setCacheStrategy("stale-while-revalidate");
    this.setCacheExpiry(30 * 60 * 1000); // 30 минут
  }

  async getMeeting(
    userId: string,
    offset = 0,
    maxSize = 50,
  ): Promise<MeetingListResponse> {
    const selectFields = [
      "assignedUserId",
      "assignedUserName",
      "dateStart",
      "dateStartDate",
      "status",
      "parentId",
      "parentType",
      "parentName",
      "name",
    ];

    return this.getList({
      select: selectFields,
      where: [
        {
          type: "bool",
          value: ["onlyMy"],
        },
      ],
      offset,
      maxSize,
      orderBy: "dateStart",
      order: "desc",
    });
  }

  async getMeetingById(meetingId: string): Promise<Meeting> {
    return this.getById(meetingId);
  }

  async createMeeting(meetingData: Partial<Meeting>): Promise<Meeting> {
    const result = await this.create(meetingData);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("meetings");
    return result;
  }

  async updateMeeting(
    meetingId: string,
    updatedData: Partial<Meeting>,
  ): Promise<Meeting> {
    const result = await this.update(meetingId, updatedData);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("meetings");
    await this.invalidateCache(meetingId);
    return result;
  }

  async deleteMeeting(meetingId: string): Promise<void> {
    await this.delete(meetingId);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("meetings");
    await this.invalidateCache(meetingId);
  }
}

export const meetingService = new MeetingService();

export type { Meeting, MeetingListResponse };
export const getMeeting = (userId: string, offset = 0, maxSize = 50) =>
  meetingService.getMeeting(userId, offset, maxSize);
export const getMeetingById = (meetingId: string) =>
  meetingService.getMeetingById(meetingId);
export const createMeeting = (meetingData: Partial<Meeting>) =>
  meetingService.createMeeting(meetingData);
export const updateMeeting = (
  meetingId: string,
  updatedData: Partial<Meeting>,
) => meetingService.updateMeeting(meetingId, updatedData);
export const deleteMeeting = (meetingId: string) =>
  meetingService.deleteMeeting(meetingId);
